import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  fetchUsers,
  addHolidayRequest,
  fetchEntitlements, // all users’ entitlements for a year
  fetchAllHolidayRequests, // all requests (we filter by user)
} from "./api";

function AddHolidayRequest() {
  const currentYear = new Date().getFullYear();

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [entitlementsMap, setEntitlementsMap] = useState(new Map()); // userId -> entitlement

  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startDayType, setStartDayType] = useState("full");
  const [endDayType, setEndDayType] = useState("full");

  // ---- Load users
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const { data } = await fetchUsers();
        setUsers(data);
        setFilteredUsers(data);
      } catch {
        toast.error("Failed to fetch users");
      }
    };
    loadUsers();
  }, []);

  // ---- Filter users by search
  useEffect(() => {
    const filtered = users.filter((u) =>
      `${u.firstName} ${u.lastName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  // ---- Load entitlements for selected year
  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await fetchEntitlements(selectedYear);
        // expected: [{ user_id, year, entitlement_hours, carried_over_hours, adjustments_hours, taken_hours, remaining_hours }]
        const map = new Map(data.map((e) => [e.user_id, e]));
        setEntitlementsMap(map);
      } catch {
        setEntitlementsMap(new Map());
        toast.error("Failed to fetch yearly entitlements");
      }
    };
    run();

    // Clear dates that don't belong to the new year
    const clampToYear = (iso) => {
      if (!iso) return "";
      return new Date(iso).getFullYear() === Number(selectedYear) ? iso : "";
    };
    setStartDate((prev) => clampToYear(prev));
    setEndDate((prev) => clampToYear(prev));
  }, [selectedYear]);

  const handleUserSelect = (user) => {
    setSelectedUserId(user.id);
    setSearchTerm(`${user.firstName} ${user.lastName}`);
    setShowDropdown(false);
  };

  // ---- Business rules
  const isDateInRestrictedRange = (d) => {
    const year = d.getFullYear();
    const a = new Date(year, 11, 24); // Dec 24
    const b = new Date(year, 11, 31); // Dec 31
    a.setHours(0, 0, 0, 0);
    b.setHours(23, 59, 59, 999);
    const x = new Date(d);
    x.setHours(12, 0, 0, 0);
    return x >= a && x <= b;
  };

  const rangesOverlap = (start1, end1, start2, end2) =>
    new Date(start1) <= new Date(end2) && new Date(start2) <= new Date(end1);

  // ---- Date handlers (clamp to selectedYear and block 24–31 Dec)
  const onStartChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    const d = new Date(val);

    if (d.getFullYear() !== Number(selectedYear)) {
      toast.error(`Start date must be within ${selectedYear}.`);
      return;
    }
    setStartDate(val);
    if (!endDate || new Date(val) > new Date(endDate)) {
      setEndDate(val);
    }
  };

  const onEndChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    const d = new Date(val);

    if (d.getFullYear() !== Number(selectedYear)) {
      toast.error(`End date must be within ${selectedYear}.`);
      return;
    }
    if (startDate && new Date(val) < new Date(startDate)) {
      setStartDate(val);
    }
    setEndDate(val);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUserId) {
      toast.error("Please select a user.");
      return;
    }
    if (!startDate || !endDate) {
      toast.error("Please choose start and end dates.");
      return;
    }

    const s = new Date(startDate);
    const eDate = new Date(endDate);

    // Single year only
    if (
      s.getFullYear() !== eDate.getFullYear() ||
      s.getFullYear() !== Number(selectedYear)
    ) {
      toast.error(
        "The request must be contained within one calendar year. Please split into two requests."
      );
      return;
    }

    // Show remaining (informational)
    const ent = entitlementsMap.get(selectedUserId);
    const remaining = Math.max(ent?.remaining_hours ?? 0, 0);

    try {
      // Overlap check for that user & year (ignore cancelled/rejected)
      const { data: allReqs } = await fetchAllHolidayRequests();
      // Backend must include hr.user_id in results
      const overlapping = allReqs.some((r) => {
        if (["cancelled", "rejected"].includes(r.status)) return false;
        if (Number(r.user_id) !== Number(selectedUserId)) return false;
        const sameYear =
          new Date(r.start_date).getFullYear() === Number(selectedYear) ||
          new Date(r.end_date).getFullYear() === Number(selectedYear);
        if (!sameYear) return false;
        return rangesOverlap(r.start_date, r.end_date, startDate, endDate);
      });
      if (overlapping) {
        toast.error(
          "This user already has a holiday request overlapping those dates."
        );
        return;
      }

      // Submit (admin endpoint accepts immediately and deducts per-year allocations)
      await addHolidayRequest({
        userId: selectedUserId,
        startDate,
        endDate,
        startDayType,
        endDayType,
      });

      toast.success("Holiday request added and entitlements updated.");
      // reset
      setSelectedUserId(null);
      setSearchTerm("");
      setStartDate("");
      setEndDate("");
      setStartDayType("full");
      setEndDayType("full");

      // refresh entitlements for the year
      const { data } = await fetchEntitlements(selectedYear);
      setEntitlementsMap(new Map(data.map((e) => [e.user_id, e])));
    } catch (err) {
      toast.error("Failed to add holiday request");
    }
  };

  const selectedEnt = selectedUserId
    ? entitlementsMap.get(selectedUserId)
    : null;
  const selectedRemaining = Math.max(selectedEnt?.remaining_hours ?? 0, 0);
  const selectedEntitlement = Number(selectedEnt?.entitlement_hours ?? 0);

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-semibold mb-6 text-center">
        Add Holiday Request for User
      </h1>

      {/* Year selector */}
      <div className="max-w-md mx-auto bg-white p-4 rounded-lg shadow mb-6 flex items-center justify-between">
        <label className="text-gray-700 font-medium">Year:</label>
        <input
          type="number"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          className="w-28 px-3 py-2 border rounded"
        />
      </div>

      <form
        onSubmit={handleSubmit}
        className="max-w-md mx-auto bg-white p-6 rounded-lg shadow-md"
      >
        {/* User autocomplete */}
        <div className="mb-4 relative">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Select User
          </label>
          <input
            type="text"
            placeholder="Type to search for a user"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowDropdown(true);
            }}
            className="border rounded w-full px-3 py-2 mb-2"
            onFocus={() => setShowDropdown(true)}
          />

          {showDropdown && filteredUsers.length > 0 && (
            <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded shadow-md max-h-40 overflow-y-auto">
              {filteredUsers.map((user) => (
                <li
                  key={user.id}
                  onClick={() => handleUserSelect(user)}
                  className="px-4 py-2 hover:bg-blue-500 hover:text-white cursor-pointer"
                >
                  {user.firstName} {user.lastName}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* If a user is selected, show entitlement info */}
        {selectedUserId && (
          <div className="mb-4 p-3 bg-gray-50 rounded border">
            <div className="text-sm text-gray-700">
              <strong>{selectedYear}</strong> entitlement:{" "}
              <strong>{selectedEntitlement}</strong> hours — remaining:{" "}
              <strong>{selectedRemaining}</strong> hours
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Requests must stay within {selectedYear} (no cross-year ranges).
            </div>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Start Date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={onStartChange}
            min={`${selectedYear}-01-01`}
            max={`${selectedYear}-12-31`}
            required
            className="border rounded w-full px-3 py-2"
            disabled={!selectedUserId}
          />
          <div className="mt-2">
            <select
              value={startDayType}
              onChange={(e) => setStartDayType(e.target.value)}
              className="border rounded w-full px-3 py-2"
              disabled={!startDate}
            >
              <option value="full">Full Day</option>
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            End Date
          </label>
          <input
            type="date"
            value={endDate}
            onChange={onEndChange}
            min={startDate || `${selectedYear}-01-01`}
            max={`${selectedYear}-12-31`}
            required
            className="border rounded w-full px-3 py-2"
            disabled={!startDate}
          />
          <div className="mt-2">
            <select
              value={endDayType}
              onChange={(e) => setEndDayType(e.target.value)}
              className="border rounded w-full px-3 py-2"
              disabled={!endDate}
            >
              <option value="full">Full Day</option>
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
            </select>
          </div>
        </div>

        <button
          type="submit"
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-full"
          disabled={!selectedUserId || !startDate || !endDate}
        >
          Add Holiday Request
        </button>
      </form>
    </div>
  );
}

export default AddHolidayRequest;
